import { useEffect } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import axios, { AxiosError, AxiosResponse } from "axios";
import { setQuery } from "../redux/actions";
import { useQueryLock } from "../../utils/network/query-lock";
import { Query } from "../../utils/network/query";
import { PictureCategory } from "../objects/picture";
import { AppState } from "../../../reducers";

export const PictureCategoriesQueryKey = 'PICTURE_CATEGORIES_QUERY_KEY';

type PictureCategoriesResponse = PictureCategory[] | {results?: PictureCategory[]};

export function usePictureCategoriesQuery(): Query<PictureCategory[]> {
    const dispatch = useDispatch();
    const clientId = useSelector((state: AppState) => state.header.client_id);
    const query = useSelector((state: AppState) => state.picture.queries.find((item) => {
        return item.key === PictureCategoriesQueryKey;
    }), shallowEqual);
    const {isLocked} = useQueryLock(PictureCategoriesQueryKey);

    useEffect(() => {
        (async () => {
            if (
                clientId !== null &&
                !isLocked()
            ) {
                dispatch(setQuery(
                    PictureCategoriesQueryKey,
                    {state: 'loading', data: null, error: null}
                ));
                try {
                    const response = await makeRequest(clientId);
                    const payload = response.data;
                    const data = Array.isArray(payload) ? payload : payload?.results;
                    dispatch(setQuery(
                        PictureCategoriesQueryKey,
                        {state: 'success', data: Array.isArray(data) ? data : [], error: null}
                    ));
                } catch (error) {
                    // Echec silencieux volontaire : tant que picture-categories/ n'est pas deploye
                    // l'API repond 404. Pas de showError, pas d'enqueueSnackbar, pas de setFinished :
                    // aucun ecran ne doit se degrader visuellement a cause de cette query.
                    dispatch(setQuery(
                        PictureCategoriesQueryKey,
                        {state: 'error', data: null, error: error as AxiosError}
                    ));
                }
            }
        })();
    }, [clientId]);

    return query?.query ?? {state: 'loading', data: null, error: null};
}

function makeRequest(clientId: number): Promise<AxiosResponse<PictureCategoriesResponse>> {
    return axios.get(
        `${API_HREF}client/${clientId}/picture-categories/`,
        {
            headers: {
                'Authorization': `Token ${localStorage.getItem('token')}`,
            },
            params: {
                limit: 1000
            }
        }
    );
}
